/**
 * Custom article show — AJAX tag tabs + pagination via Section Rendering API.
 * Syncs active tab label into section title + hero subtitle.
 */
(function () {
  'use strict';

  var SECTION_TYPE = 'custom-article-show';
  var loadingClass = 'is-loading';
  var enteringClass = 'is-entering';
  var activeController = null;
  var loadToken = 0;
  var lastTitleLabel = '';

  function normalizePath(url) {
    try {
      var parsed = new URL(url, window.location.origin);
      var path = parsed.pathname.replace(/\/+$/, '') + parsed.search;
      return path || '/';
    } catch (e) {
      return url;
    }
  }

  function sectionRoot() {
    return document.querySelector('[data-section-type="' + SECTION_TYPE + '"]');
  }

  function shopifySectionEl(sectionId) {
    if (!sectionId) return null;
    return document.getElementById('shopify-section-' + sectionId);
  }

  function parseSection(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function sectionFetchUrl(pageUrl, sectionId) {
    var url = new URL(pageUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function tabLabel(tab) {
    if (!tab) return '';
    return (tab.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function blogTitle(root) {
    root = root || sectionRoot();
    if (!root) return '';
    return (root.getAttribute('data-blog-title') || '').trim();
  }

  function isAllTab(tab) {
    return !!(tab && tab.getAttribute('data-tab') === 'all');
  }

  function setSectionTitle(label, root) {
    if (!label) return;
    lastTitleLabel = label;
    root = root || sectionRoot();
    if (!root) return;
    var title = root.querySelector('.custom-article-show__title');
    if (title) title.textContent = label;
  }

  function setHeroSubtitle(label) {
    if (!label) return;
    document.querySelectorAll('.hero-banner .main__subtitle').forEach(function (el) {
      el.textContent = label;
    });
  }

  function syncFromTab(tab, root) {
    root = root || sectionRoot();
    if (!tab) return;
    var label = tabLabel(tab);
    if (!label) return;

    setSectionTitle(label, root);

    // Untagged / All blog URL: hero shows the blog name (Journal, News, etc.)
    if (isAllTab(tab)) {
      setHeroSubtitle(blogTitle(root) || label);
      return;
    }

    setHeroSubtitle(label);
  }

  function syncFromActiveTab(root) {
    root = root || sectionRoot();
    if (!root) return;
    var active =
      root.querySelector('.custom-article-show__tab.is-active') ||
      root.querySelector('.custom-article-show__tab[data-tab="all"]');
    if (!active) return;
    syncFromTab(active, root);
  }

  function markNoTransition(root) {
    if (!root) return;
    root.querySelectorAll('.custom-article-show__tab, .custom-article-show__pagination a').forEach(function (el) {
      el.classList.add('js-no-transition');
    });
  }

  function applyInstantTabUi(href) {
    var root = sectionRoot();
    if (!root) return null;
    var target = normalizePath(href);
    var matched = null;

    root.querySelectorAll('.custom-article-show__tab').forEach(function (tab) {
      var tabHref = tab.getAttribute('href');
      if (!tabHref) return;
      var isActive = normalizePath(tabHref) === target;
      tab.classList.toggle('is-active', isActive);
      if (isActive) {
        tab.setAttribute('aria-current', 'page');
        matched = tab;
      } else {
        tab.removeAttribute('aria-current');
      }
    });

    return matched;
  }

  function replaceSection(html, sectionId) {
    var doc = parseSection(html);
    var next =
      doc.getElementById('shopify-section-' + sectionId) ||
      doc.querySelector('[data-section-type="' + SECTION_TYPE + '"]');
    var current =
      shopifySectionEl(sectionId) ||
      (sectionRoot() && sectionRoot().closest('.shopify-section'));

    if (!current || !next) {
      throw new Error('Could not find section to replace');
    }

    var imported = document.importNode(next, true);
    imported.classList.add(enteringClass);
    current.replaceWith(imported);

    var fresh =
      document.getElementById('shopify-section-' + sectionId) ||
      document.querySelector('[data-section-type="' + SECTION_TYPE + '"]');

    if (fresh) {
      markNoTransition(fresh);
      var inner = fresh.querySelector('[data-section-type="' + SECTION_TYPE + '"]') || fresh;
      if (lastTitleLabel) {
        setSectionTitle(lastTitleLabel, inner);
        var activeAfter =
          inner.querySelector('.custom-article-show__tab.is-active') ||
          inner.querySelector('.custom-article-show__tab[data-tab="all"]');
        if (activeAfter && isAllTab(activeAfter)) {
          setHeroSubtitle(blogTitle(inner) || lastTitleLabel);
        } else {
          setHeroSubtitle(lastTitleLabel);
        }
      } else {
        syncFromActiveTab(inner);
      }
      void fresh.offsetWidth;
      requestAnimationFrame(function () {
        fresh.classList.remove(enteringClass);
        inner.classList.remove(loadingClass);
        inner.removeAttribute('aria-busy');
      });
    }

    return fresh;
  }

  function clearLoading(sectionId) {
    var wrap = shopifySectionEl(sectionId);
    var root = sectionRoot();
    [wrap, root].forEach(function (el) {
      if (!el) return;
      el.classList.remove(loadingClass);
      el.removeAttribute('aria-busy');
    });
  }

  function loadArticles(pageUrl, options) {
    options = options || {};
    var root = sectionRoot();
    if (!root) return Promise.resolve();

    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return Promise.resolve();

    if (activeController) {
      try {
        activeController.abort();
      } catch (e) {}
    }

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    activeController = controller;
    var token = ++loadToken;

    if (options.instantTab !== false) {
      var matchedTab = applyInstantTabUi(pageUrl);
      if (matchedTab) {
        syncFromTab(matchedTab, root);
      } else if (options.tab) {
        syncFromTab(options.tab, root);
      }
    } else if (options.tab) {
      syncFromTab(options.tab, root);
    }

    if (options.updateHistory !== false) {
      var nextUrl = new URL(pageUrl, window.location.origin);
      history.pushState({ customArticleShow: true }, '', nextUrl.pathname + nextUrl.search);
    }

    var wrap = shopifySectionEl(sectionId);
    var loadingTarget = wrap || root;
    loadingTarget.classList.add(loadingClass);
    loadingTarget.setAttribute('aria-busy', 'true');

    var fetchUrl = sectionFetchUrl(pageUrl, sectionId);
    var fetchOpts = { credentials: 'same-origin' };
    if (controller) fetchOpts.signal = controller.signal;

    return fetch(fetchUrl, fetchOpts)
      .then(function (res) {
        if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        if (token !== loadToken) return;
        replaceSection(html, sectionId);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (token !== loadToken) return;
        console.error('[custom-article-show] AJAX failed:', err, fetchUrl);
        window.location.href = pageUrl;
      })
      .finally(function () {
        if (token !== loadToken) return;
        if (activeController === controller) activeController = null;
        clearLoading(sectionId);
      });
  }

  function isModifiedClick(event) {
    return (
      (event.button !== undefined && event.button !== 0) ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    );
  }

  function onDelegatedClick(event) {
    if (isModifiedClick(event)) return;

    var tab = event.target.closest('.custom-article-show__tab');
    var pageLink = event.target.closest('.custom-article-show__pagination a');
    var link = tab || pageLink;
    if (!link) return;

    var root = link.closest('[data-section-type="' + SECTION_TYPE + '"]');
    if (!root) return;

    var href = link.getAttribute('href');
    if (!href || href === '#') return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (normalizePath(window.location.href) === normalizePath(href)) return;

    if (tab) syncFromTab(tab, root);

    loadArticles(href, { instantTab: !!tab, tab: tab || null });
  }

  function init() {
    var root = sectionRoot();
    if (!root) return;
    markNoTransition(root.closest('.shopify-section') || root);
    syncFromActiveTab(root);
  }

  document.addEventListener('click', onDelegatedClick, true);

  window.addEventListener('popstate', function () {
    if (!sectionRoot()) return;
    loadArticles(window.location.href, { updateHistory: false, instantTab: true });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
